<ng-container *transloco="let t">
  <div
    id="progress-stepper-mobile"
    [class]="mobileClass()"
    role="progressbar"
    [attr.aria-valuenow]="currentStepIndex()"
    [attr.aria-valuemin]="0"
    [attr.aria-valuemax]="steps().length - 1"
  >
    <button
      id="stepper-mobile-header"
      type="button"
      class="w-full py-2 flex items-center gap-4 cursor-pointer hover:bg-ld-neutral-50 transition-colors"
      (click)="toggleMobileExpanded()"
      [attr.aria-expanded]="isMobileExpanded()"
      aria-controls="stepper-mobile-steps"
    >
      <div class="flex-1 text-left min-w-0">
        <p class="font-dm text-xs text-ld-neutral-500 mb-0.5">
          {{ t('progressStepperStep') }} {{ currentStep().index + 1 }} {{ t('progressStepperOf') }} {{ steps().length }}
        </p>
        <p class="font-dm font-semibold text-base text-ld-primary truncate">
          {{ currentStep().step.label }}
        </p>
      </div>

      <div class="flex items-center shrink-0">
        <svg
          class="w-5 h-5 text-ld-neutral-400 transition-transform duration-200"
          [class.rotate-180]="isMobileExpanded()"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </button>

    @if (isMobileExpanded()) {
      <div id="stepper-mobile-steps" class="border-t border-ld-neutral-200 py-3">
        @for (step of stepsWithPosition(); track step.id; let last = $last) {
          <div
            [id]="'stepper-mobile-step-' + step.index"
            class="flex items-center gap-3 py-2.5"
            [class.border-b]="!last"
            [class.border-ld-neutral-200]="!last"
          >
            <div class="size-6 flex items-center justify-center shrink-0">
              @switch (step.status) {
                @case ('completed') {
                  <ld-svg-icon
                    [src]="checkmarkIcon"
                    [attr.aria-label]="t('progressStepperCompleted')"
                    class="size-6 text-ld-primary"
                  />
                }
                @case ('current') {
                  <div class="size-6 rounded-full border-2 border-ld-primary"></div>
                }
                @default {
                  <div class="size-6 rounded-full border-2 border-ld-neutral-300"></div>
                }
              }
            </div>

            @if (step.status === 'completed' && allowNavigation()) {
              <button
                type="button"
                [id]="'stepper-mobile-nav-' + step.index"
                class="flex-1 text-left cursor-pointer font-dm text-sm text-ld-neutral hover:text-ld-primary transition-colors"
                (click)="onStepClick(step)"
              >
                {{ step.label }}
              </button>
            } @else {
              <span
                class="flex-1 font-dm text-sm"
                [class.font-semibold]="step.status === 'current'"
                [class.text-ld-primary]="step.status === 'current'"
                [class.text-ld-neutral]="step.status === 'completed'"
                [class.text-ld-neutral-400]="step.status === 'not-completed'"
              >
                {{ step.label }}
              </span>
            }
          </div>
        }
      </div>
    }
  </div>

  <div
    id="progress-stepper-desktop"
    [class]="desktopClass()"
    role="progressbar"
    [attr.aria-valuenow]="currentStepIndex()"
    [attr.aria-valuemin]="0"
    [attr.aria-valuemax]="steps().length - 1"
  >
    <div id="stepper-indicators-row" class="flex items-center w-full min-w-fit">
      @for (step of stepsWithPosition(); track step.id) {
        <div [id]="'stepper-step-' + step.index" class="flex items-center flex-1 min-w-20 xl:min-w-[100px]">
          <div
            [id]="'stepper-bar-left-' + step.index"
            [class]="getProgressBarClass(step, 'left')"
            [class.opacity-0]="step.isFirst"
          ></div>

          <div
            [id]="'stepper-indicator-' + step.index"
            class="size-7 xl:size-8 flex items-center justify-center shrink-0"
          >
            @switch (step.status) {
              @case ('completed') {
                <ld-svg-icon
                  [src]="checkmarkIcon"
                  [attr.aria-label]="t('progressStepperCompleted')"
                  class="size-7 xl:size-8 text-ld-primary"
                />
              }
              @case ('current') {
                <div class="size-7 xl:size-8 rounded-full border-2 border-ld-primary flex items-center justify-center">
                  <div class="size-3.5 xl:size-4 rounded-full bg-ld-primary"></div>
                </div>
              }
              @default {
                <div class="size-7 xl:size-8 rounded-full border-2 border-ld-neutral-200"></div>
              }
            }
          </div>

          <div
            [id]="'stepper-bar-right-' + step.index"
            [class]="getProgressBarClass(step, 'right')"
            [class.opacity-0]="step.isLast"
          ></div>
        </div>
      }
    </div>

    <div id="stepper-labels-row" class="flex items-start w-full min-w-fit mt-2">
      @for (step of stepsWithPosition(); track step.id) {
        <div [id]="'stepper-label-' + step.index" class="flex-1 text-center px-1 xl:px-2 min-w-20 xl:min-w-[100px]">
          @if (step.status === 'completed' && allowNavigation()) {
            <button
              type="button"
              [id]="'stepper-nav-btn-' + step.index"
              class="cursor-pointer font-dm text-[11px] xl:text-xs leading-tight font-medium text-ld-neutral hover:text-ld-primary hover:underline transition-colors bg-transparent border-none p-0"
              [attr.aria-label]="'Go to ' + step.label + ' section'"
              (click)="onStepClick(step)"
            >
              {{ step.label }}
            </button>
          } @else {
            <p
              class="font-dm text-[11px] xl:text-xs leading-tight"
              [class.font-semibold]="step.status === 'current'"
              [class.text-ld-primary]="step.status === 'current'"
              [class.font-medium]="step.status !== 'current'"
              [class.text-ld-neutral]="step.status !== 'current'"
            >
              {{ step.label }}
            </p>
          }
        </div>
      }
    </div>
  </div>
</ng-container>
